import { HelpCircle, Plus, X } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import FlowContext from '../contextFlow'
import { convertEdgeToRouteNodeWorkflow } from '../utils/edgeToRouteConverter'
import ConditionBuilder from './conditionBuilder/conditionBuilder'

function EdgeSideBar({ edgeData, setIsSideBarOpen }) {
  const { setIsEditingEdge, triggerUpdate, elements, embedMode }
    = useContext(FlowContext)

  const sourceNode = edgeData?.source

  const isRouteNode
    = elements.nodes.find(node => node.id === sourceNode)?.type === 'Route'

  const maxChars = 100
  const [pathwayLabel, setPathwayLabel] = useState(edgeData?.data?.label || '')
  const [description, setDescription] = useState(
    edgeData?.data?.description || '',
  )
  const [mode, setMode] = useState(
    edgeData?.data?.condition ? 'condition' : 'label',
  )
  const [conditions, setConditions] = useState(() => {
    const oldConditions = edgeData?.data?.condition
    return oldConditions || []
  })

  const [alwaysPick, setAlwaysPick] = useState(
    edgeData?.data?.alwaysPick || false,
  )

  // Sync label edge value with sidebar label value
  useEffect(() => {
    const textarea = document.querySelector('textarea[aria-editing-label="true"]')

    const handleEditingEdgeLabel = (event) => {
      setPathwayLabel(event.target.value)
    }
    textarea?.addEventListener('input', handleEditingEdgeLabel)

    return () => {
      textarea?.removeEventListener('input', handleEditingEdgeLabel)
    }
  }, [])

  // Wrapper function to call the utility
  const handleConvertClick = () => {
    convertEdgeToRouteNodeWorkflow({
      edgeData,
      currentConditions: conditions,
      elements,
      triggerUpdate,
      setIsSideBarOpen,
      setIsEditingEdge,
    })
  }

  const onSubmit = () => {
    let isWarnToast = false

    const source_node_id = edgeData.source

    const source_node = elements.nodes.find(
      node => node.id === source_node_id,
    )

    if (source_node.type === 'Route') {
      isWarnToast = true
      toast.warn(
        'You cannot edit the conditions here. Please edit the conditions in the Route node instead.',
      )
      return
    }

    const updatedEdges = elements.edges.map((element_edge) => {
      if (element_edge.id === edgeData.id) {
        const edge = { ...element_edge }
        const sourceId = edge.source
        const sourceEdges = elements.edges.filter(e => e.source === sourceId)
        const hasCondition = sourceEdges.some(e => e.data?.condition)
        const doesNotHaveCondition = sourceEdges.some(
          e => !e.data?.condition,
        )

        if (mode === 'label') {
          if (!pathwayLabel || pathwayLabel.trim() === '') {
            isWarnToast = true
            toast.warn('Please enter a pathway label')
            return element_edge
          }

          if (hasCondition) {
            isWarnToast = true
            toast.warn(
              'You cannot have pathways with both the \'Variable Comparison\' mode and \'Pathway Label\' from the same node. Please remove the pathways using the \'Variable Comparison\' from the source node first, before adding a new pathway with \'Pathway Label\'.',
            )
            return element_edge
          }

          if (pathwayLabel.length > maxChars) {
            isWarnToast = true
            toast.warn(
              `Pathway label must be less than ${maxChars} characters`,
            )
            return element_edge
          }
          edge.data.label = pathwayLabel.trim()
          edge.data.description = description
          if (description) {
            edge.data.description = description.trim()
          }
          if (alwaysPick) {
            edge.data.alwaysPick = true
          }
          else {
            delete edge.data.alwaysPick
          }
          delete edge.data.condition
        }
        else if (mode === 'condition') {
          if (doesNotHaveCondition && sourceEdges.length > 1) {
            isWarnToast = true
            toast.warn(
              'You cannot have pathways with both the \'Variable Comparison\' mode and \'Pathway Label\' from the same node. Please remove the pathways using the \'Pathway Label\' from the source node first, before adding a new pathway with \'Variable Comparison\' mode.',
            )
            return element_edge
          }

          const validConditions = conditions.every(
            cond => cond.field && cond.value,
          )
          if (!validConditions) {
            isWarnToast = true
            toast.warn('Please complete all condition fields')
            return element_edge
          }

          edge.data.condition = conditions

          edge.data.label = isRouteNode
            ? conditions
                .map((cond) => {
                  if (cond.isGroup) {
                    return `(${cond.conditions
                      .map(
                        (groupCond, i) =>
                          `${i > 0 ? 'And ' : ''}${groupCond.field} ${groupCond.operator} ${groupCond?.value}`,
                      )
                      .join(' ')})`
                  }
                  return `${cond.field} ${cond.operator} ${cond?.value}`
                })
                .join(' Or ')
            : conditions
                .map(
                  (cond, index) =>
                    `${index > 0 ? `${cond.operator} ` : ''}${cond.field} ${cond.conditionOperator} ${cond.value}`,
                )
                .join(' ')
          delete edge.data.description
        }
        return edge
      }
      return element_edge
    })

    if (isWarnToast) {
      return
    }

    if (alwaysPick) {
      updatedEdges.forEach((edge) => {
        if (edge.source === edgeData.source && edge.id !== edgeData.id) {
          delete edge.data.alwaysPick
        }
        if (edge.id === edgeData.id) {
          edge.data.alwaysPick = true
        }
      })
    }

    triggerUpdate({ edges: updatedEdges })
    setIsEditingEdge(null)
    setIsSideBarOpen(false)
  }

  const deleteCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index))
  }

  const handlePathwayLabelChange = (event) => {
    if (event.target.value.length <= maxChars) {
      setPathwayLabel(event.target.value)
    }
  }

  const handleConditionChange = (index, field, value) => {
    const newConditions = [...conditions]
    newConditions[index][field] = value

    // Clear the value when operator changes to 'is'
    if (field === 'conditionOperator' && value === 'is') {
      console.log('is', newConditions[index].value)
      newConditions[index].value = 'null'
    }

    setConditions(newConditions)
  }

  const addCondition = () => {
    setConditions([
      ...conditions,
      { operator: 'AND', field: '', conditionOperator: 'contains', value: '' },
    ])
  }

  return (
    <div
      className={`
        fixed top-0 right-0 z-50 h-screen w-1/4 overflow-y-auto border-l
        border-slate-200 bg-slate-100 shadow-lg
      `}
    >
      <div className="flex flex-col gap-y-5 p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">Edit Pathway</h2>
          <button
            type="button"
            onClick={() => {
              setIsSideBarOpen(false)
              setIsEditingEdge(null)
            }}
            className={`
              text-slate-500 transition-colors
              hover:text-slate-700
            `}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-y-2.5">
          <label className="block text-sm font-semibold text-slate-700">
            How should the agent decide?
          </label>
          <div className="flex items-center text-xs text-slate-600">
            Choose how the agent should decide which pathway to take.
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="ml-1.5 h-3 w-3 text-slate-400" />
                </TooltipTrigger>
                <TooltipContent className={`
                  max-w-xs rounded bg-white p-2.5 shadow-lg
                `}
                >
                  <p className="mb-1.5 text-xs">
                    <strong>Pathway label:</strong>
                    {' '}
                    Uses natural language to
                    decide (e.g. User said yes)
                  </p>
                  <p className="text-xs">
                    <strong>Variable Comparison:</strong>
                    {' '}
                    Uses Mathematical
                    expressions / logic operators to proceed to this pathway if
                    the variable values meets a certain criteria (e.g. variable
                    &gt; 5)
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex rounded-md bg-white shadow-xs">
            <button
              type="button"
              className={`
                flex-1 rounded-l-lg px-4 py-2 text-xs font-medium
                focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                focus:outline-hidden
                ${
    mode === 'label'
      ? 'border-indigo-200 bg-indigo-100 text-indigo-700'
      : `
        text-slate-600
        hover:bg-slate-50 hover:text-slate-800
      `
    }
              `}
              onClick={() => setMode('label')}
            >
              Pathway Label
            </button>
            <button
              type="button"
              className={`
                flex-1 rounded-r-lg px-4 py-2 text-xs font-medium
                focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                focus:outline-hidden
                ${
    mode === 'condition'
      ? 'border-indigo-200 bg-indigo-100 text-indigo-700'
      : `
        text-slate-600
        hover:bg-slate-50 hover:text-slate-800
      `
    }
              `}
              onClick={() => setMode('condition')}
            >
              Advanced Settings
            </button>
          </div>
        </div>

        {mode === 'condition' && !isRouteNode && (
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800">
                Value Comparison
              </h3>
              <button
                type="button"
                onClick={addCondition}
                className={`
                  flex items-center text-xs font-medium text-indigo-600
                  transition-colors
                  hover:text-indigo-800
                `}
              >
                <Plus className="mr-1.5 h-3 w-3" />
                {' '}
                Add Condition
              </button>
            </div>
            <p className="text-xs text-slate-600">
              Compare the value of existing variables. If it meets the criteria
              (evaluates to true), the agent will proceed to this pathway.
            </p>

            <div className={`
              my-4 flex flex-col gap-y-2 rounded-md border border-amber-200
              bg-amber-50 p-3 text-xs text-amber-700
            `}
            >
              <p className="font-semibold">Important: This method of variable comparison is being deprecated!</p>
              <p>
                We now recommend using
                {' '}
                <strong className="font-semibold">Route Nodes</strong>
                {' '}
                for more powerful and flexible conditions.
              </p>
              <p className="mt-2 font-medium">Benefits of Route Nodes:</p>
              <ul className="flex list-inside list-disc flex-col gap-y-1 pl-2">
                <li>Enables global node support (unlike the current variable comparison).</li>
                <li>Allows for fallback paths.</li>
                <li>Provides a central place to manage all routes and conditions.</li>
              </ul>
            </div>

            <button
              type="button"
              onClick={handleConvertClick}
              className={`
                mb-4 flex w-full items-center justify-center rounded-md
                bg-indigo-500 px-3 py-2 text-xs font-medium text-white shadow-xs
                transition-colors duration-150 ease-in-out
                hover:bg-indigo-600
              `}
            >
              Convert to Route Node
            </button>

            {conditions.map((condition, index) => (
              <div
                key={index}
                className={`
                  flex flex-col gap-y-2.5 rounded-md bg-white p-4 shadow-xs
                `}
              >
                {index > 0 && (
                  <div className="flex items-center gap-x-2">
                    <label className="text-xs font-medium text-slate-700">
                      Logic operator:
                    </label>
                    <select
                      className={`
                        rounded- mt-0.5 block w-full border-slate-300 py-1.5
                        pr-6 pl-2 text-xs
                        focus:border-indigo-500 focus:ring-indigo-500
                        focus:outline-hidden
                      `}
                      value={condition.operator}
                      onChange={e =>
                        handleConditionChange(index, 'operator', e.target.value)}
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  </div>
                )}
                <div className="flex items-center gap-x-2">
                  <input
                    type="text"
                    placeholder="Variable to compare e.g. age"
                    className={`
                      mt-0.5 block w-full rounded border border-slate-300
                      bg-white px-2.5 py-2 text-xs placeholder-slate-400
                      shadow-xs
                      focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500
                      focus:outline-hidden
                    `}
                    value={condition.field}
                    onChange={e =>
                      handleConditionChange(index, 'field', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => deleteCondition(index)}
                    className={`
                      text-slate-400 transition-colors
                      hover:text-slate-600
                    `}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <select
                  className={`
                    rounded- mt-0.5 block w-full border-slate-300 py-2 pr-6 pl-2
                    text-xs
                    focus:border-indigo-500 focus:ring-indigo-500
                    focus:outline-hidden
                  `}
                  value={condition.conditionOperator || 'contains'}
                  onChange={e =>
                    handleConditionChange(
                      index,
                      'conditionOperator',
                      e.target.value,
                    )}
                >
                  <option value="" disabled>Select operator</option>
                  <option value="contains">contains</option>
                  <option value="does not contain">does not contain</option>
                  <option value="equals">equals</option>
                  <option value="not equals">does not equal</option>
                  <option value="greater than">is greater than</option>
                  <option value="less than">is less than</option>
                  <option value="is">is</option>
                </select>
                {condition.conditionOperator === 'is'
                  ? (
                      <select
                        className={`
                          rounded- mt-0.5 block w-full border-slate-300 py-2
                          pr-6 pl-2 text-xs
                          focus:border-indigo-500 focus:ring-indigo-500
                          focus:outline-hidden
                        `}
                        value={condition.value || 'null'}
                        onChange={e =>
                          handleConditionChange(index, 'value', e.target.value)}
                      >
                        <option value="" disabled>Select value</option>
                        <option
                          value="null"
                          className={`
                            font-medium tracking-wide text-slate-700 uppercase
                          `}
                        >
                          NULL
                        </option>
                        <option
                          value="not null"
                          className={`
                            font-medium tracking-wide text-slate-700 uppercase
                          `}
                        >
                          !NULL
                        </option>
                        <option
                          value="true"
                          className={`
                            font-medium tracking-wide text-slate-700 uppercase
                          `}
                        >
                          TRUE
                        </option>
                        <option
                          value="false"
                          className={`
                            font-medium tracking-wide text-slate-700 uppercase
                          `}
                        >
                          FALSE
                        </option>
                        <option
                          value="undefined"
                          className={`
                            font-medium tracking-wide text-slate-700 uppercase
                          `}
                        >
                          UNDEFINED
                        </option>
                        <option
                          value="not undefined"
                          className={`
                            font-medium tracking-wide text-slate-700 uppercase
                          `}
                        >
                          !UNDEFINED
                        </option>
                      </select>
                    )
                  : (
                      <input
                        type="text"
                        placeholder="Value"
                        className={`
                          mt-0.5 block w-full rounded border border-slate-300
                          bg-white px-2.5 py-2 text-xs placeholder-slate-400
                          shadow-xs
                          focus:border-indigo-500 focus:ring-1
                          focus:ring-indigo-500 focus:outline-hidden
                        `}
                        value={condition.value}
                        onChange={e =>
                          handleConditionChange(index, 'value', e.target.value)}
                      />
                    )}
              </div>
            ))}
          </div>
        )}

        {mode === 'condition' && isRouteNode && (
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800">
                Value Comparison
              </h3>
            </div>
            <p className="text-xs text-slate-600">
              Compare the value of existing variables. If it meets the criteria
              (evaluates to true), the agent will proceed to this pathway.
            </p>
            <ConditionBuilder
              conditions={conditions}
              onChange={setConditions}
            />
          </div>
        )}

        {mode === 'label' && (
          <div className="flex flex-col gap-y-4">
            <div>
              <label
                htmlFor="label"
                className="block text-sm font-semibold text-slate-700"
              >
                Pathway Label
              </label>
              <p className="mt-1.5 text-xs text-slate-600">
                Enter a label that describes when this pathway should be chosen.
                Keep it short and succinct e.g. user said yes
              </p>
              <textarea
                id="label"
                className={`
                  mt-1.5 block w-full rounded border border-slate-300 bg-white
                  px-2.5 py-2 text-xs placeholder-slate-400 shadow-xs
                  focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500
                  focus:outline-hidden
                `}
                rows={3}
                value={pathwayLabel}
                onChange={handlePathwayLabelChange}
                maxLength={maxChars}
                placeholder="Enter pathway label"
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {pathwayLabel.length}
                /
                {maxChars}
              </p>
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-slate-700"
              >
                Description
              </label>
              <p className="mt-1.5 text-xs text-slate-600">
                Provide more information and describe when this pathway should
                be chosen. This is optional but gives the agent more context
                about when to choose this pathway.
              </p>
              <textarea
                id="description"
                className={`
                  mt-1.5 block w-full rounded border border-slate-300 bg-white
                  px-2.5 py-2 text-xs placeholder-slate-400 shadow-xs
                  focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500
                  focus:outline-hidden
                `}
                rows={4}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe when this pathway should be chosen..."
              />
            </div>

            <div className="flex items-center gap-x-1.5">
              <Switch
                className="bg-gray-300"
                id="always-pick"
                checked={alwaysPick}
                onCheckedChange={checked => setAlwaysPick(checked)}
              />
              <label
                htmlFor="always-pick"
                className="text-xs font-medium text-slate-700"
              >
                Always pick this pathway
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-3 w-3 text-slate-400" />
                  </TooltipTrigger>
                  <TooltipContent className={`
                    max-w-xs rounded bg-white p-2.5 shadow-lg
                  `}
                  >
                    <p className="text-xs">
                      When enabled, the agent will always choose this pathway,
                      ignoring other options. Only one pathway from a source
                      node can have this option enabled.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}

        <div className="pt-4">
          <div className="flex justify-end gap-x-2.5">
            <button
              type="button"
              className={`
                rounded border border-slate-300 bg-white px-4 py-2 text-xs
                font-medium text-slate-700 shadow-xs
                hover:bg-slate-50
                focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                focus:outline-hidden
              `}
              onClick={() => {
                setIsSideBarOpen(false)
                setIsEditingEdge(null)
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`
                inline-flex justify-center rounded border border-transparent
                bg-indigo-600 px-4 py-2 text-xs font-medium text-white shadow-xs
                hover:bg-indigo-700
                focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
                focus:outline-hidden
              `}
              onClick={onSubmit}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EdgeSideBar
